import {
  commissionService,
  type CommissionBatchUpdateItem,
  type CommissionDetailUpdateRequest,
  type CommissionInfoResponse,
  type CommissionSearchRequest,
} from '@/entities/commission/api/commission.api';
import type { Pageable } from '@/entities/user/api/user.api';
import { usePaginatedQuery } from '@/shared/hooks/usePaginatedQuery';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

export const commissionKeys = {
  all: ['commissions'] as const,
  lists: () => [...commissionKeys.all, 'list'] as const,
  list: (search: CommissionSearchRequest, pageable: Pageable) =>
    [...commissionKeys.lists(), search, pageable] as const,
};

export interface CommissionListParams {
  search: CommissionSearchRequest;
  pageable: Pageable;
}

/**
 * 정산 수수료 목록 조회.
 * 레거시 `settlementFeeInfo.js` — 기준년월·검색 필터 + 서버 페이지네이션.
 */
export const useCommissionList = ({ search, pageable }: CommissionListParams) =>
  usePaginatedQuery<CommissionSearchRequest, CommissionInfoResponse>({
    queryKeyPrefix: commissionKeys.lists(),
    search,
    pageable,
    queryFn: async ({ search: s, pageable: p }) =>
      commissionService.getCommissions(s, p),
  });

/**
 * 정산 수수료 목록 엑셀.
 * 서버가 파일을 만들 때까지 기다리므로 버튼 로딩을 이 mutation에 묶는다.
 */
export const useExportCommissions = () =>
  useMutation({
    mutationFn: (search: CommissionSearchRequest) =>
      commissionService.exportCommissions(search),
  });

/** 정산 수수료 상세 — 레거시 `settlementFeeDetails.js` */
export const useCommissionDetail = (
  request: { refYm: string; drugCode: string } | null,
) =>
  useQuery({
    queryKey: request
      ? [...commissionKeys.all, 'detail', request.refYm, request.drugCode]
      : [],
    queryFn: async () => {
      if (!request) throw new Error('params required');
      return commissionService.getCommissionDetail(request);
    },
    enabled: !!request?.refYm && !!request?.drugCode,
  });

/** 정산 수수료 상세 수정 — PATCH /admin/commissions/detail */
export const useUpdateCommissionDetail = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      refYm,
      drugCode,
      payload,
    }: {
      refYm: string;
      drugCode: string;
      payload: CommissionDetailUpdateRequest;
    }) => commissionService.updateCommissionDetail(refYm, drugCode, payload),
    onSuccess: (_, { refYm, drugCode }) => {
      queryClient.invalidateQueries({
        queryKey: [...commissionKeys.all, 'detail', refYm, drugCode],
      });
      queryClient.invalidateQueries({ queryKey: commissionKeys.all });
    },
  });
};

/** 수수료 일괄 수정 — PATCH /admin/commissions/batch */
export const useBatchUpdateCommissions = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (items: CommissionBatchUpdateItem[]) =>
      commissionService.updateCommissionBatch(items),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: commissionKeys.all });
    },
  });
};
